<script lang="ts">
  import { flip } from "svelte/animate";
  import { fly } from "svelte/transition";
  import { ACCENT_PALETTE, SECS_PER_PAGE } from "@piano/shared";
  import { formatDuration, plural } from "../lib/format.js";
  import { router } from "../lib/router.svelte.js";
  import { library, type Piece } from "../lib/stores/library.svelte.js";
  import { live } from "../lib/stores/live.svelte.js";
  import { playlists } from "../lib/stores/playlists.svelte.js";
  import { ui } from "../lib/stores/ui.svelte.js";
  import Cover from "../components/Cover.svelte";
  import Icon from "../components/Icon.svelte";
  import Sheet from "../components/Sheet.svelte";

  let { id }: { id: string } = $props();
  const playlist = $derived(playlists.byId.get(id));
  const pieces = $derived(
    (playlist?.items ?? []).map((docId) => library.byId.get(docId)).filter((p): p is Piece => Boolean(p)),
  );
  const totalSeconds = $derived(
    pieces.reduce((sum, p) => sum + (p.pageCount ?? 4) * (p.secsPerPage ?? SECS_PER_PAGE.default), 0),
  );
  /** Gerät, das diese Setlist gerade spielt (Live-Session) */
  const playingHere = $derived(live.others.find((p) => p.playlistId === id) ?? null);
  const currentIndex = $derived(playingHere?.index ?? playlist?.currentIndex ?? -1);

  // ---------- Umsortieren per Ziehen ----------
  const ROW = 64;
  let order = $state<string[]>([]);
  let drag = $state<{ id: string; from: number; dy: number } | null>(null);
  let startY = 0;
  $effect(() => {
    if (!drag) order = pieces.map((p) => p.id);
  });
  const displayed = $derived.by(() => {
    if (!drag) return order;
    const to = Math.max(0, Math.min(order.length - 1, drag.from + Math.round(drag.dy / ROW)));
    const next = order.filter((x) => x !== drag!.id);
    next.splice(to, 0, drag.id);
    return next;
  });

  function dragStart(event: PointerEvent, docId: string): void {
    (event.currentTarget as HTMLElement).setPointerCapture(event.pointerId);
    startY = event.clientY;
    drag = { id: docId, from: order.indexOf(docId), dy: 0 };
  }
  function dragMove(event: PointerEvent): void {
    if (drag) drag = { ...drag, dy: event.clientY - startY };
  }
  function dragEnd(): void {
    if (!drag) return;
    const next = displayed;
    const changed = next.join() !== order.join();
    drag = null;
    order = next;
    if (changed && playlist) void playlists.reorder(playlist.id, next);
  }
  /** Versatz der gezogenen Zeile relativ zu ihrem neuen Platz */
  function dragOffset(docId: string): number {
    if (!drag || drag.id !== docId) return 0;
    return drag.from * ROW + drag.dy - displayed.indexOf(docId) * ROW;
  }

  function play(index: number): void {
    const docId = playlist?.items[index];
    if (!playlist || !docId) return;
    router.go(`/noten/${docId}?setlist=${playlist.id}&i=${index}`);
  }

  // ---------- Bearbeiten ----------
  let editing = $state(false);
  let editName = $state("");
  let editIcon = $state("");
  let editColor = $state("");
  function openEdit(): void {
    if (!playlist) return;
    editName = playlist.name;
    editIcon = playlist.icon;
    editColor = playlist.accentColor;
    editing = true;
  }
  async function saveEdit(event: SubmitEvent): Promise<void> {
    event.preventDefault();
    if (!playlist) return;
    const ok = await playlists.update(playlist.id, {
      name: editName.trim() || playlist.name,
      icon: editIcon.trim() || playlist.icon,
      accentColor: editColor,
    });
    if (ok) editing = false;
  }
  async function remove(): Promise<void> {
    if (!playlist) return;
    const ok = await ui.confirm({
      title: "Setlist löschen?",
      message: `„${playlist.name}“ wird gelöscht. Die Noten selbst bleiben erhalten.`,
      confirmLabel: "Löschen",
      danger: true,
    });
    if (!ok) return;
    editing = false;
    if (await playlists.remove(playlist.id)) router.go("/setlists", { replace: true });
  }
</script>

{#if playlist}
  <div class="setlist" class:narrow={!ui.wide} style:--tint={playlist.accentColor}>
    <header class="hero">
      {#if !ui.wide}
        <button
          type="button"
          class="icon-btn back"
          aria-label="Zurück"
          onclick={() => router.back("/setlists")}><Icon name="back" /></button
        >
      {/if}
      <div class="mosaic">
        {#each pieces.slice(0, 4) as piece (piece.id)}<img src={piece.thumb} alt="" />{/each}
        {#if pieces.length === 0}<span class="mosaic-icon">{playlist.icon}</span>{/if}
      </div>
      <div class="hero-text">
        <span class="kicker">Setlist{playlists.activeId === playlist.id ? " · aktuell" : ""}</span>
        <h1>{playlist.name}</h1>
        <span class="meta"
          >{plural(pieces.length, "Stück", "Stücke")}{pieces.length
            ? ` · ${formatDuration(totalSeconds)}`
            : ""}</span
        >
      </div>
    </header>

    <div class="actions">
      <button
        type="button"
        class="play"
        aria-label="Setlist spielen"
        disabled={!pieces.length}
        onclick={() => play(Math.max(0, playlist.currentIndex))}
      >
        <Icon name="play" size={26} />
      </button>
      {#if playlists.activeId !== playlist.id}
        <button type="button" class="btn btn-ghost" onclick={() => playlists.setActive(playlist.id)}
          >Als aktuelle Setlist</button
        >
      {/if}
      <button type="button" class="btn btn-ghost" onclick={openEdit}>Bearbeiten</button>
      {#if playingHere}
        <span class="live"><span class="dot"></span> Läuft auf dem {playingHere.device}</span>
      {/if}
    </div>

    {#if pieces.length === 0}
      <div class="empty">
        <h3>Noch leer</h3>
        <p class="t2">Füge Stücke über das „+“ oder das Aktionsmenü in der Bibliothek hinzu.</p>
        <button type="button" class="btn btn-primary" onclick={() => router.go("/")}>Zur Bibliothek</button>
      </div>
    {:else}
      <ol class="rows" style:height="{displayed.length * ROW}px" aria-label="Stücke der Setlist">
        {#each displayed as docId, index (docId)}
          {@const piece = library.byId.get(docId) ?? {
            thumb: "",
            name: "Unbekanntes Stück",
            artist: "",
            pageCount: 0,
          }}
          <li
            class="row"
            class:current={index === currentIndex}
            class:dragging={drag?.id === docId}
            style:transform={drag?.id === docId ? `translateY(${dragOffset(docId)}px) scale(1.02)` : null}
            animate:flip={{ duration: drag?.id === docId ? 0 : 280 }}
            in:fly={{ y: 10, duration: 300, delay: Math.min(index, 12) * 30 }}
          >
            <span class="num">
              {#if index === currentIndex}<Icon
                  name={playingHere ? "tablet" : "play"}
                  size={16}
                />{:else}{index + 1}{/if}
            </span>
            <button type="button" class="open" onclick={() => play(index)}>
              <span class="mini"><Cover src={piece.thumb} ratio="1" radius="6px" /></span>
              <span class="text">
                <span class="title ellipsis">{piece.name}</span>
                <span class="t3 artist ellipsis">{piece.artist}</span>
              </span>
            </button>
            {#if ui.wide}
              <span class="t3 pages">{piece.pageCount ? `${piece.pageCount} S.` : ""}</span>
            {/if}
            <button
              type="button"
              class="icon-btn small"
              aria-label="„{piece.name}“ entfernen"
              onclick={() => playlists.removeItem(playlist.id, docId)}
            >
              <Icon name="minus" size={18} stroke={2.6} />
            </button>
            <span
              class="handle"
              role="button"
              tabindex="0"
              aria-label="„{piece.name}“ verschieben"
              onpointerdown={(e) => dragStart(e, docId)}
              onpointermove={dragMove}
              onpointerup={dragEnd}
              onpointercancel={dragEnd}
              onkeydown={(e) => {
                const i = order.indexOf(docId);
                const to = e.key === "ArrowUp" ? i - 1 : e.key === "ArrowDown" ? i + 1 : -1;
                if (to < 0 || to >= order.length) return;
                e.preventDefault();
                const next = order.filter((x) => x !== docId);
                next.splice(to, 0, docId);
                order = next;
                void playlists.reorder(playlist.id, next);
              }}
            >
              <Icon name="grip" size={18} />
            </span>
          </li>
        {/each}
      </ol>
    {/if}
  </div>

  {#if editing}
    <Sheet title="Setlist bearbeiten" onclose={() => (editing = false)} width={440}>
      <form class="edit" onsubmit={saveEdit}>
        <h2>Setlist bearbeiten</h2>
        <label class="field">Name<input class="input" bind:value={editName} maxlength="120" required /></label
        >
        <label class="field"
          >Symbol (Emoji)<input class="input emoji" bind:value={editIcon} maxlength="16" /></label
        >
        <div class="field">
          Farbe
          <div class="palette">
            {#each ACCENT_PALETTE as color (color)}
              <button
                type="button"
                class="color"
                style:background={color}
                aria-label="Farbe {color}"
                aria-pressed={editColor.toUpperCase() === color}
                onclick={() => (editColor = color)}
              ></button>
            {/each}
          </div>
        </div>
        <div class="edit-actions">
          <button type="button" class="btn btn-danger" onclick={remove}
            ><Icon name="trash" size={18} /> Löschen</button
          >
          <span class="grow"></span>
          <button type="button" class="btn btn-ghost" onclick={() => (editing = false)}>Abbrechen</button>
          <button type="submit" class="btn btn-primary">Speichern</button>
        </div>
      </form>
    </Sheet>
  {/if}
{:else if playlists.loaded}
  <div class="empty missing">
    <h3>Setlist nicht gefunden</h3>
    <button type="button" class="btn btn-ghost" onclick={() => router.go("/setlists", { replace: true })}
      >Zu den Setlists</button
    >
  </div>
{/if}

<style>
  .hero {
    position: relative;
    display: flex;
    align-items: flex-end;
    gap: 28px;
    padding: calc(40px + var(--safe-top)) 32px 28px;
    background: color-mix(in oklab, var(--tint) 55%, #0b0b0d);
    animation: rise var(--dur-sheet) var(--ease-out) both;
  }
  .narrow .hero {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
    padding: calc(12px + var(--safe-top)) 18px 20px;
  }
  .back {
    color: var(--text);
    margin-left: -10px;
  }
  .mosaic {
    position: relative;
    width: 196px;
    flex: 0 0 196px;
    aspect-ratio: 1;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2px;
    border-radius: 12px;
    overflow: hidden;
    background: var(--tint);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.5);
  }
  .narrow .mosaic {
    width: 140px;
    flex-basis: auto;
    align-self: center;
  }
  .mosaic img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
    background: var(--paper);
  }
  .mosaic-icon {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 64px;
  }
  .hero-text {
    display: flex;
    flex-direction: column;
    gap: 10px;
    min-width: 0;
  }
  .kicker {
    font-size: 12px;
    font-weight: 800;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.78);
  }
  h1 {
    font-size: 56px;
    line-height: 1;
    word-break: break-word;
  }
  .narrow h1 {
    font-size: 32px;
  }
  .meta {
    font-size: 14px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.82);
  }
  .actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;
    padding: 20px 32px 8px;
  }
  .narrow .actions {
    padding: 16px 18px 6px;
  }
  .play {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    background: var(--accent);
    color: var(--on-accent);
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-shadow: 0 10px 26px rgba(0, 0, 0, 0.4);
    transition: transform var(--dur-base) var(--ease-pop);
  }
  .play:hover:not(:disabled) {
    transform: scale(1.06);
  }
  .play:active:not(:disabled) {
    transform: scale(0.92);
  }
  .live {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    font-weight: 800;
    color: var(--accent-3);
  }
  .dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: var(--accent-3);
  }
  .rows {
    list-style: none;
    margin: 8px 0 30px;
    padding: 0 22px;
    position: relative;
  }
  .narrow .rows {
    padding: 0 10px;
  }
  .row {
    height: 64px;
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 0 8px 0 10px;
    border-radius: 10px;
    background: var(--surface);
    transition:
      background var(--dur-fast),
      box-shadow var(--dur-fast);
  }
  .row:hover {
    background: #19191d;
  }
  .row.dragging {
    position: relative;
    z-index: 5;
    background: var(--raised-2);
    box-shadow: 0 18px 40px rgba(0, 0, 0, 0.55);
  }
  .row.current .title,
  .row.current .num {
    color: var(--accent);
  }
  .num {
    width: 24px;
    text-align: center;
    font-weight: 700;
    font-size: 14px;
    color: var(--text-2);
    display: flex;
    justify-content: center;
  }
  .open {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
    text-align: left;
  }
  .mini {
    width: 46px;
    flex: 0 0 46px;
  }
  .text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }
  .title {
    font-weight: 700;
    font-size: 15px;
  }
  .artist {
    font-size: 13px;
    font-weight: 600;
  }
  .pages {
    font-size: 13px;
    font-weight: 700;
    width: 56px;
    text-align: right;
  }
  .small {
    width: 38px;
    height: 38px;
  }
  .handle {
    width: 40px;
    height: 44px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-3);
    touch-action: none;
    cursor: grab;
  }
  .handle:active {
    cursor: grabbing;
    color: var(--text);
  }
  .empty {
    padding: 50px 20px;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 10px;
    align-items: center;
  }
  .empty h3 {
    font-size: 22px;
  }
  .empty p {
    margin: 0;
  }
  .missing {
    padding-top: 120px;
  }
  .edit {
    display: flex;
    flex-direction: column;
    gap: 14px;
    padding: 0 8px;
  }
  .edit h2 {
    font-size: 22px;
  }
  .emoji {
    width: 90px;
    font-size: 22px;
  }
  .palette {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
  }
  .color {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    transition: transform var(--dur-base) var(--ease-pop);
  }
  .color[aria-pressed="true"] {
    transform: scale(1.18);
    box-shadow:
      0 0 0 3px #1c1c21,
      0 0 0 5px var(--text);
  }
  .edit-actions {
    display: flex;
    gap: 10px;
    align-items: center;
    flex-wrap: wrap;
    margin-top: 6px;
  }
  .grow {
    flex: 1;
  }
</style>
